import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  ColumnFiltersState,
  RowSelectionState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Backdrop, Box, Divider, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Pagination from '../shared/pagination';
import { CardTable, cardTableContainerSx } from '../shared/card-table';
import { DEFAULT_PAGE_SIZE, getRowStyling } from '../shared/utils';
import { globalFilterFn } from '../../../util/filter';
import { ApiError } from '../../../util';
import { Loading } from '../../atomic';
import { Button } from '../../atomic/button';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { ConfirmationDialog } from '../../compound';
import { useDialog } from '../../compound/dialogs/confirmationDialog/useDialog';
import { isOnboardingOpen } from '../../compound/pxtraCardOnboarding';
import { CONSENT_RETURN_MESSAGE } from '../../../../pages/benefits/pxtraCardConsentReturn';
import { useStore } from '../../../../hooks/useStore';
import {
  CardApprovalDecision,
  CardApprovalRequest,
  useDecideCardApprovals,
  useCardOnboarding,
  useListCardApprovals
} from '../../../../queries/pxtraCard';
import { useCardApprovalColumns } from './columns';
import { HeadlineToolBar } from '../shared/headline-toolbar';
import { ApprovalSelectionSummary } from './selection-summary';
import { EXPIRY_WARNING_DAYS, daysUntil, failureText, isDecidable } from './utils';

type PendingDecision = {
  selection: CardApprovalRequest[];
  decision: CardApprovalDecision;
};

const CONSENT_POLL_INTERVAL_MS = 3000;
// Long enough for a signature at Swan, short enough that a manager who walked
// away is not left staring at a spinner.
const CONSENT_TIMEOUT_MS = 3 * 60 * 1000;

// The checkbox ships inside a FormControlLabel, whose -11px margin is meant to
// pull a label back into line. With no label it drags the box past the edge.
const containerSx = cardTableContainerSx({
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: 0
  }
});

export const CardApprovalTable = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const companyId = companyStore.company?.id ?? '';

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Kept on screen as a link as well as opened: a pop-up blocker must not be
  // able to hide the one step that makes the cards real.
  const [consent, setConsent] = useState<{ url: string; requestIds: string[] } | null>(null);
  // What is at Swan waiting for a signature, and since when.
  const [signing, setSigning] = useState<{ requestIds: string[]; since: number } | null>(null);
  const [signingTimedOut, setSigningTimedOut] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  // Nothing about the signature reaches this tab on its own: the manager signs
  // at Swan, Swan calls the backend, and the list only learns about it by
  // asking again.
  const { data: onboarding } = useCardOnboarding(companyId);
  const onboardingOpen = isOnboardingOpen(onboarding);

  const { data, isLoading, isError, refetch } = useListCardApprovals(companyId, {
    // Coming back from the other tab is the moment the answer is most likely to
    // have changed, and an interval alone would miss it: the browser stops the
    // timer while this tab sits in the background.
    refetchOnWindowFocus: !!signing,
    refetchInterval: signing ? CONSENT_POLL_INTERVAL_MS : false
  });
  const { mutate: decide, isPending: isDeciding } = useDecideCardApprovals(companyId);

  const canApprove =
    !!data?.approver.has_swan_permission && !!data?.approver.identification_completed;

  const requests = data?.results ?? [];

  useEffect(() => {
    if (!signing) return;

    const results = data?.results ?? [];
    // Open means unsigned. Once none of them are, the signature landed, no
    // matter how the manager got back here.
    const unsigned = signing.requestIds.some((id) =>
      results.some((request) => request.id === id && request.status === 'open')
    );

    if (!unsigned) {
      setSigning(null);
      setRowSelection({});

      return;
    }

    // A signature that is never given would otherwise hold the overlay for the
    // rest of the session.
    const timer = setTimeout(
      () => {
        setSigning(null);
        setSigningTimedOut(true);
      },
      Math.max(0, signing.since + CONSENT_TIMEOUT_MS - Date.now())
    );

    return () => clearTimeout(timer);
  }, [signing, data]);

  useEffect(() => {
    const onReturn = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data !== CONSENT_RETURN_MESSAGE) return;

      // The window says it is done, so ask at once instead of sitting out the
      // rest of the poll interval.
      void refetch();
    };

    window.addEventListener('message', onReturn);

    return () => window.removeEventListener('message', onReturn);
  }, [refetch]);

  const startSigning = (requestIds: string[]) => {
    setSigningTimedOut(false);
    setSigning({ requestIds, since: Date.now() });
  };

  /** What a decision that opened no consent actually did. Without this the
   * screen simply drops the selection and says nothing. */
  const reportDecided = (
    results: CardApprovalRequest[],
    decision: CardApprovalDecision,
    failed: number
  ) => {
    const settledStatus = decision === 'reject' ? 'rejected' : 'approved';
    const settled = results.filter((request) => request.status === settledStatus).length;

    if (settled > 0) {
      enqueueSnackbar(
        t(decision === 'reject' ? 'pxtraCard:decisionRejected' : 'pxtraCard:decisionApproved', {
          count: settled
        }),
        { variant: 'success' }
      );

      return;
    }

    // Nothing moved and nothing broke: the cards are at Swan already and the
    // signature is what is missing.
    if (failed === 0 && results.some((request) => request.awaiting_signature)) {
      enqueueSnackbar(t('pxtraCard:awaitingSignatureText'), { variant: 'warning' });
    }
  };

  const applyDecision = (selection: CardApprovalRequest[], decision: CardApprovalDecision) => {
    if (selection.length === 0) return;

    const requestIds = selection.map((request) => request.id);
    // Opened while the click is still the reason anything happens: a window
    // opened from the answer is treated as a pop-up and blocked. It starts
    // blank and gets the consent as soon as the backend returns one. A
    // rejection and a physical card whose PIN the employee chooses ask Swan
    // nothing, so nothing opens for them — an empty tab that flashes open and
    // shut reads as a broken button.
    const needsConsent =
      decision !== 'reject' && selection.some((request) => request.requires_swan_consent);
    const consentTab = needsConsent ? window.open('', '_blank') : null;

    decide(
      { requestIds, decision },
      {
        onSuccess: (result) => {
          const failed = result.results.filter((request) => request.status === 'failed');

          failed.forEach((request) =>
            enqueueSnackbar(
              t('pxtraCard:issuingFailedFor', {
                name: `${request.first_name} ${request.last_name}`,
                reason: failureText(request, t)
              }),
              { variant: 'error' }
            )
          );

          if (!result.consent_url) {
            consentTab?.close();
            setRowSelection({});
            reportDecided(result.results, decision, failed.length);

            return;
          }

          if (consentTab) {
            consentTab.location.href = result.consent_url;
            // The selection stays until the signature lands, so the overlay can
            // name what is being signed and the manager keeps their place.
            startSigning(requestIds);

            return;
          }

          // Only reachable where the browser refused the window. Without this
          // the consent would be unreachable and the cards would never exist.
          setConsent({ url: result.consent_url, requestIds });
        },
        onError: (error) => {
          consentTab?.close();
          // The backend says what it refused; "could not be saved" makes the
          // manager guess at a reason it already knows.
          enqueueSnackbar(
            error instanceof ApiError ? error.message : t('pxtraCard:decisionFailedTitle'),
            { variant: 'error' }
          );
        }
      }
    );
  };

  const {
    dialogRelevantValue: pendingDecision,
    setDialogRelevantValue: askDecision,
    dialogProps: decisionDialogProps
  } = useDialog<PendingDecision>();
  const isRejecting = pendingDecision?.decision === 'reject';
  const pendingCount = pendingDecision?.selection.length ?? 0;
  const asksSwan =
    !isRejecting &&
    (pendingDecision?.selection.some((request) => request.requires_swan_consent) ?? false);

  // The decision runs inside the confirm click: the Swan tab it opens is only
  // let through by the browser while that click is still the reason.
  const confirmDecision = () => {
    if (!pendingDecision) return;

    applyDecision(pendingDecision.selection, pendingDecision.decision);
  };

  const columns = useCardApprovalColumns({
    canApprove,
    onApprove: (request: CardApprovalRequest) =>
      askDecision({
        selection: [request],
        decision: request.status === 'failed' ? 'retry' : 'approve'
      }),
    onReject: (request: CardApprovalRequest) =>
      askDecision({ selection: [request], decision: 'reject' })
  });

  const table = useReactTable({
    data: requests,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    enableRowSelection: (row) => canApprove && isDecidable(row.original),
    sortDescFirst: false,
    state: {
      pagination,
      globalFilter,
      columnFilters,
      rowSelection
    }
  });

  const openConsent = () => {
    if (!consent) return;

    // Opened from the click so no blocker intervenes, and by script so the
    // return page is allowed to close it again.
    window.open(consent.url, '_blank', 'noopener=no');
    startSigning(consent.requestIds);
    setConsent(null);
  };

  const selected = table.getSelectedRowModel().rows.map((row) => row.original);
  const expiringSoon = requests.filter((request) => {
    const days = daysUntil(request.expires_on);

    // A request without a deadline is not close to one.
    return request.status === 'open' && days !== null && days <= EXPIRY_WARNING_DAYS;
  }).length;

  if (isLoading) {
    return (
      <Paper className="p-8">
        <Box className="flex justify-center items-center">
          <Loading />
        </Box>
      </Paper>
    );
  }

  if (isError) {
    return (
      <FeedbackAlert
        severity="error"
        title={t('pxtraCard:loadFailedTitle')}
        description={t('pxtraCard:loadFailedText')}
      />
    );
  }

  return (
    <>
      <Backdrop
        open={isDeciding || !!signing}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        className="flex-col gap-y-3 text-white"
      >
        {/* The spinner lays itself out at full height, which would push the
            message to the bottom edge of the overlay. */}
        <Box className="h-14">
          <Loading />
        </Box>
        <Box className="text-base">
          {signing
            ? t('pxtraCard:consentWaitingText', { count: signing.requestIds.length })
            : t('pxtraCard:decidingText')}
        </Box>
      </Backdrop>

      {/* Only worth saying when there is something to act on: with an empty
          list the manager has nothing to approve anyway, and while the Swan
          check runs the onboarding notice above already explains it. */}
      {!canApprove && !onboardingOpen && requests.length > 0 && (
        <Box className="mb-3">
          <FeedbackAlert
            severity="warning"
            title={t('pxtraCard:missingPermissionTitle')}
            description={t('pxtraCard:missingPermissionText')}
          />
        </Box>
      )}

      {consent && (
        <Box className="mb-3">
          <FeedbackAlert
            severity="warning"
            title={t('pxtraCard:consentPendingTitle')}
            description={t('pxtraCard:consentPendingText')}
            customActionElement
            actionElement={
              <Button size="small" onClick={openConsent}>
                {t('pxtraCard:consentOpen')}
              </Button>
            }
          />
        </Box>
      )}

      {signingTimedOut && (
        <Box className="mb-3">
          <FeedbackAlert
            severity="warning"
            title={t('pxtraCard:consentTimeoutTitle')}
            description={t('pxtraCard:consentTimeoutText')}
            onClose={() => setSigningTimedOut(false)}
          />
        </Box>
      )}

      {expiringSoon > 0 && (
        <Box className="mb-3">
          <FeedbackAlert
            severity="info"
            title={t('pxtraCard:expiringSoonTitle', { count: expiringSoon })}
            description={t('pxtraCard:expiringSoonText')}
          />
        </Box>
      )}

      <Paper className="overflow-hidden flex flex-col w-full min-w-0 h-full">
        <HeadlineToolBar table={table} headline="pxtraCard:request" resetsSelection />

        {selected.length > 0 && (
          <ApprovalSelectionSummary
            selected={selected}
            canApprove={canApprove}
            onApprove={() => askDecision({ selection: selected, decision: 'approve' })}
            onReject={() => askDecision({ selection: selected, decision: 'reject' })}
          />
        )}

        <CardTable
          table={table}
          containerSx={containerSx}
          emptyTitle={t('pxtraCard:emptyTitle')}
          emptyText={t('pxtraCard:emptyHelperText')}
          rowClassName={(row) => getRowStyling({ row })}
        />
        <Divider className="border-grey-100" />
        <Pagination table={table} />
      </Paper>

      <ConfirmationDialog
        {...decisionDialogProps}
        variant={isRejecting ? 'warning' : 'question'}
        width="400px"
        title={t(isRejecting ? 'pxtraCard:rejectConfirmTitle' : 'pxtraCard:approveConfirmTitle', {
          count: pendingCount
        })}
        contentText={
          isRejecting
            ? t('pxtraCard:rejectConfirmText', { count: pendingCount })
            : asksSwan
              ? t('pxtraCard:approveConfirmText')
              : undefined
        }
        confirmButtonText={t(isRejecting ? 'pxtraCard:reject' : 'pxtraCard:approve')}
        onConfirm={confirmDecision}
      />
    </>
  );
});
